import React, { useRef } from 'react';
import html2pdf from 'html2pdf.js';
import {
  Container, Box, Typography, Paper, Avatar, Divider, Button,
  useTheme, useMediaQuery
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { FaCcVisa, FaCcMastercard, FaCcPaypal } from 'react-icons/fa';
import GiftCardPdf from '../giftcards/GiftCardPdf';

const paymentIcons = {
  visa: <FaCcVisa size={32} />, 
  mastercard: <FaCcMastercard size={32} />, 
  paypal: <FaCcPaypal size={32} />,
};

export default function GiftCardOrderSuccess({ order, piDetails }) {
  const theme       = useTheme();
  const isMobile    = useMediaQuery(theme.breakpoints.down('sm'));
  const receiptRef  = useRef();

  // Extract gift card details
 const code       = order.code;
  const value      = order.items[0]?.subtotal ?? order.total;
  const card       = piDetails?.payment_method?.card;  const issuedDate  = order.createdAt ? new Date(order.createdAt).toLocaleDateString() : '';


  const downloadReceipt = () => {
    if (!receiptRef.current) return;
    html2pdf()
      .set({
        margin: 10,
        filename: `giftcard_receipt_${order.id}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      })
      .from(receiptRef.current)
      .save();
  };

  return (
    <Container maxWidth="lg" sx={{ py: isMobile ? 4 : 8, backgroundColor: '#f4f4f4' }}>
      <Paper sx={{ p: isMobile ? 3 : 6, borderRadius: 3 }} elevation={2}>
        {/* Header */}
        <Box textAlign="center" mb={4}>
          <Avatar sx={{ bgcolor: '#000', width: 64, height: 64, mx: 'auto' }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 40, color: '#fff' }} />
          </Avatar>
          <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ mt: 2, fontWeight: 800 }}>
            Gift Card Purchased
          </Typography>
          <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 500, color: '#555' }}>
            Order ID: <strong>{order.id}</strong>
          </Typography>
        </Box>

        {/* Content Grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4 }}>
          {/* Gift Card Details */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
              Gift Card Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography><strong>Code:</strong> {code}</Typography>
            <Typography sx={{ mt: 1 }}><strong>Value:</strong> €{value.toFixed(2)}</Typography>
            {issuedDate && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Issued on {issuedDate}
              </Typography>
            )}

            {/* Actions */}
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={downloadReceipt}
                sx={{ fontWeight: 600 }}
              >
                Download Receipt
              </Button>
              <GiftCardPdf
                code={code}
                amount={value}
                issuedAt={order.createdAt}
              />
            </Box>
          </Box>

          {/* Payment & Summary */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper elevation={0} sx={{ p: 3, backgroundColor: '#fff', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                Payment Method
              </Typography>
              {card ? (
                <Box display="flex" alignItems="center" gap={2}>
                  {paymentIcons[card.brand.toLowerCase()]}
                  <Box>
                    <Typography fontWeight={600}>
                      {card.brand.toUpperCase()} ••••{card.last4}
                    </Typography>
                    <Typography variant="body2">
                      Expires {String(card.exp_month).padStart(2, '0')}/{card.exp_year}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Typography>Loading payment info…</Typography>
              )}
            </Paper>

            <Paper elevation={0} sx={{ p: 3, backgroundColor: '#fff', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                Summary
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body1" fontWeight={700}>
                Total Paid: €{order.total.toFixed(2)}
              </Typography>
            </Paper>
          </Box>
        </Box>
      </Paper>

      {/* Hidden PDF Template */}
      <div
        ref={receiptRef}
        style={{ position: 'absolute', top: '-9999px', width: '210mm', padding: '10mm', background: '#fff' }}
      >
        <Paper sx={{ p: 4 }}>
          <Typography variant="h5">Gift Card Receipt</Typography>
          <Divider sx={{ my: 2 }} />
          <Typography>Order ID: {order.id}</Typography>
          <Typography>Value: €{value.toFixed(2)}</Typography>
        </Paper>
      </div>
    </Container>
  );
}
